import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  X, Plus, Trash, DotsSixVertical, FloppyDisk, ChartLine, TextT, Check,
} from "@phosphor-icons/react";

let _cid = 0;
const nextCid = () => `c${Date.now()}-${_cid++}`;

function buildCols(columns = [], rows = []) {
  const n = rows.length;
  return (columns.length ? columns : ["Column 1"]).map((name, ci) => ({
    cid: nextCid(),
    name: name || `Column ${ci + 1}`,
    keep: true,
    cells: Array.from({ length: n }, (_, r) => (rows[r] && rows[r][ci] != null ? String(rows[r][ci]) : "")),
  }));
}

const PIB = [
  { v: "", n: "None (general spreadsheet)" },
  { v: "iip", n: "PIB — IIP" },
  { v: "wpi", n: "PIB — WPI" },
  { v: "cpi", n: "PIB — CPI" },
];

export default function ExcelEditor({ initial, onSave, onClose }) {
  const [name, setName] = useState(initial?.name || "Spreadsheet");
  const [pibCategory, setPibCategory] = useState(initial?.pib_category || "");
  const [chartType, setChartType] = useState(initial?.chart_type || "line");
  const [cols, setCols] = useState(() => buildCols(initial?.columns, initial?.rows));
  const rowCount = useMemo(() => cols.reduce((m, c) => Math.max(m, c.cells.length), 0), [cols]);
  const [labelCid, setLabelCid] = useState(() => {
    const kept = buildColsLabelInit(initial, cols);
    return kept.label;
  });
  const [valueCid, setValueCid] = useState(() => {
    const kept = buildColsLabelInit(initial, cols);
    return kept.value;
  });
  const [saving, setSaving] = useState(false);
  const dragCol = useRef(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const setCell = (ci, ri, val) => setCols((cs) => cs.map((c, i) => {
    if (i !== ci) return c;
    const cells = [...c.cells]; cells[ri] = val; return { ...c, cells };
  }));
  const rename = (ci, val) => setCols((cs) => cs.map((c, i) => (i === ci ? { ...c, name: val } : c)));
  const toggleKeep = (ci) => setCols((cs) => cs.map((c, i) => (i === ci ? { ...c, keep: !c.keep } : c)));
  const addColumn = () => setCols((cs) => [...cs, { cid: nextCid(), name: `Column ${cs.length + 1}`, keep: true, cells: Array.from({ length: rowCount }, () => "") }]);
  const addRow = () => setCols((cs) => cs.map((c) => ({ ...c, cells: [...c.cells, ""] })));
  const deleteColumn = (ci) => setCols((cs) => (cs.length > 1 ? cs.filter((_, i) => i !== ci) : cs));
  const deleteRow = (ri) => setCols((cs) => cs.map((c) => ({ ...c, cells: c.cells.filter((_, r) => r !== ri) })));

  const moveColumn = (from, to) => {
    if (from == null || to == null || from === to) return;
    setCols((cs) => {
      const arr = [...cs];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
  };
  const setPosition = (ci, posStr) => {
    const pos = parseInt(posStr, 10);
    if (Number.isNaN(pos)) return;
    const to = Math.max(0, Math.min(cols.length - 1, pos - 1));
    moveColumn(ci, to);
  };

  const save = async () => {
    const kept = cols.filter((c) => c.keep);
    if (!kept.length) return;
    const columns = kept.map((c) => c.name || "Column");
    const rows = Array.from({ length: rowCount }, (_, r) => kept.map((c) => c.cells[r] ?? ""));
    let labelIdx = kept.findIndex((c) => c.cid === labelCid);
    let valueIdx = kept.findIndex((c) => c.cid === valueCid);
    if (labelIdx < 0) labelIdx = 0;
    if (valueIdx < 0) valueIdx = kept.length > 1 ? 1 : 0;
    setSaving(true);
    try {
      await onSave({
        id: initial?.id, name, month: initial?.month,
        columns, rows, pib_category: pibCategory, chart_type: chartType,
        chart_label_col: labelIdx, chart_value_col: valueIdx,
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6" data-testid="excel-editor">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[1200px] max-h-[92vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex-1 min-w-0">
            <div className="label-caps text-slate-500 mb-1">Spreadsheet name</div>
            <Input data-testid="excel-name" value={name} onChange={(e) => setName(e.target.value)} className="h-10 max-w-md" />
          </div>
          <div>
            <div className="label-caps text-slate-500 mb-1">PIB category</div>
            <select data-testid="excel-pib-category" value={pibCategory} onChange={(e) => setPibCategory(e.target.value)}
              className="h-10 rounded-lg border border-slate-200 bg-white text-slate-800 text-sm px-3 outline-none">
              {PIB.map((p) => <option key={p.v} value={p.v}>{p.n}</option>)}
            </select>
          </div>
          <div>
            <div className="label-caps text-slate-500 mb-1">Chart type</div>
            <select data-testid="excel-chart-type" value={chartType} onChange={(e) => setChartType(e.target.value)}
              className="h-10 rounded-lg border border-slate-200 bg-white text-slate-800 text-sm px-3 outline-none">
              <option value="line">Line</option>
              <option value="bar">Bar</option>
            </select>
          </div>
          <button data-testid="excel-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 ml-1"><X size={22} /></button>
        </div>

        {/* toolbar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 flex-wrap">
          <Button data-testid="excel-add-column" onClick={addColumn} className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg h-9 px-3 gap-1.5 text-sm"><Plus size={15} weight="bold" /> Add Column</Button>
          <Button data-testid="excel-add-row" onClick={addRow} className="bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg h-9 px-3 gap-1.5 text-sm"><Plus size={15} weight="bold" /> Add Row</Button>
          <span className="text-xs text-slate-400 ml-2">Untick a column to exclude it. Drag the handle or set a position number to reorder.</span>
          {pibCategory && (
            <span className="text-[11px] ml-auto flex items-center gap-1.5 text-slate-500">
              <ChartLine size={14} className="text-[#004182]" /> Bar chart: X = <b className="text-[#004182]">{cols.find((c) => c.cid === labelCid)?.name || "?"}</b> (months) · each other numeric column = a grouped bar
            </span>
          )}
        </div>

        {/* grid */}
        <div className="flex-1 overflow-auto p-4">
          <table className="border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white z-10 w-10 text-[11px] text-slate-400 font-normal p-1">#</th>
                {cols.map((c, ci) => (
                  <th key={c.cid} data-testid={`excel-col-${ci}`}
                    onDragOver={(e) => { if (dragCol.current != null) { e.preventDefault(); setOverIdx(ci); } }}
                    onDrop={(e) => { e.preventDefault(); moveColumn(dragCol.current, ci); dragCol.current = null; setDragIdx(null); setOverIdx(null); }}
                    className={`min-w-[160px] align-top p-2 rounded-t-lg border-b-2 ${overIdx === ci && dragIdx !== ci ? "border-[#F59E0B] bg-amber-50" : "border-slate-200"} ${dragIdx === ci ? "opacity-40" : ""} ${c.keep ? "bg-slate-50" : "bg-slate-100/60"}`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <div draggable data-testid={`excel-col-drag-${ci}`}
                        onDragStart={() => { dragCol.current = ci; setDragIdx(ci); }}
                        onDragEnd={() => { dragCol.current = null; setDragIdx(null); setOverIdx(null); }}
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#F59E0B]" title="Drag to reorder">
                        <DotsSixVertical size={16} weight="bold" />
                      </div>
                      <button data-testid={`excel-col-keep-${ci}`} onClick={() => toggleKeep(ci)}
                        title={c.keep ? "Included — click to exclude" : "Excluded — click to include"}
                        className={`h-5 w-5 rounded flex items-center justify-center border ${c.keep ? "bg-[#004182] border-[#004182] text-white" : "bg-white border-slate-300 text-transparent"}`}>
                        <Check size={12} weight="bold" />
                      </button>
                      <input data-testid={`excel-col-pos-${ci}`} type="number" min={1} max={cols.length}
                        value={ci + 1} onChange={(e) => setPosition(ci, e.target.value)}
                        className="w-11 h-6 text-[11px] text-center rounded border border-slate-200 text-slate-600 outline-none" title="Set column position" />
                      <button data-testid={`excel-col-del-${ci}`} onClick={() => deleteColumn(ci)} className="ml-auto text-slate-300 hover:text-red-500"><Trash size={14} /></button>
                    </div>
                    <input data-testid={`excel-col-name-${ci}`} value={c.name} onChange={(e) => rename(ci, e.target.value)}
                      className={`w-full h-8 px-2 rounded border text-sm font-semibold outline-none ${c.keep ? "border-slate-200 text-slate-800 bg-white" : "border-slate-200 text-slate-400 bg-transparent line-through"}`} />
                    {pibCategory && (
                      <div className="flex items-center gap-2 mt-1.5 text-[10px]">
                        <button data-testid={`excel-col-x-${ci}`} onClick={() => setLabelCid(c.cid)}
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${labelCid === c.cid ? "bg-[#004182] text-white" : "bg-slate-100 text-slate-500"}`}><TextT size={11} /> X (months)</button>
                        {labelCid !== c.cid && <span className="text-slate-400 flex items-center gap-1"><ChartLine size={11} className="text-[#F59E0B]" /> bar</span>}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }).map((_, ri) => (
                <tr key={ri} data-testid={`excel-row-${ri}`} className="group">
                  <td className="sticky left-0 bg-white z-10 text-[11px] text-slate-400 text-center p-1 align-middle">
                    <span className="group-hover:hidden">{ri + 1}</span>
                    <button data-testid={`excel-row-del-${ri}`} onClick={() => deleteRow(ri)} className="hidden group-hover:inline text-slate-300 hover:text-red-500"><Trash size={13} /></button>
                  </td>
                  {cols.map((c, ci) => (
                    <td key={c.cid} className="p-0.5">
                      <input data-testid={`excel-cell-${ri}-${ci}`} value={c.cells[ri] ?? ""}
                        onChange={(e) => setCell(ci, ri, e.target.value)}
                        className={`w-full h-8 px-2 rounded border border-transparent hover:border-slate-200 focus:border-[#004182] text-sm outline-none ${c.keep ? "text-slate-800" : "text-slate-300"}`} />
                    </td>
                  ))}
                </tr>
              ))}
              {rowCount === 0 && (
                <tr><td colSpan={cols.length + 1} className="text-center text-slate-400 py-8 text-sm">No rows. Click "Add Row" to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50">
          <Button data-testid="excel-cancel" onClick={onClose} className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 rounded-xl h-11 px-5">Cancel</Button>
          <Button data-testid="excel-save" onClick={save} disabled={saving} className="gold-btn rounded-xl h-11 px-6 gap-2 font-semibold">
            <FloppyDisk size={18} weight="bold" /> {saving ? "Saving…" : "Save to Repository"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function buildColsLabelInit(initial, cols) {
  // pick chart label/value cids from initial indices, else defaults
  const li = initial?.chart_label_col;
  const vi = initial?.chart_value_col;
  const label = cols[li != null ? li : 0]?.cid || cols[0]?.cid;
  const value = cols[vi != null ? vi : (cols.length > 1 ? 1 : 0)]?.cid || cols[0]?.cid;
  return { label, value };
}
